// =============================================================================
// Damage Dice Size Progression — Pathfinder 1e / D&D 3.5 rules
//
// Rules (publisher FAQ):
//   • +1 size step → +2 chart steps (or +1 if initial size ≤ Small OR damage ≤ 1d6)
//   • −1 size step → −2 chart steps (or −1 if initial size ≤ Medium OR damage ≤ 1d8)
//   • d6 off-chart → use next-lowest d6 count as Xd8 proxy
//   • d8 off-chart → use next-highest d8 count as Xd6 proxy
//   • d4: 2d4=1d8, 3d4=2d6, etc.
//   • d12: Nd12 = 2N d6
//   • d10 special: Nd10 increases to (2N)d8, decreases to Nd8 (bypass chart)
// =============================================================================

export type WeaponSize =
  | 'fine' | 'diminutive' | 'tiny' | 'small' | 'medium'
  | 'large' | 'huge' | 'gargantuan' | 'colossal';

export const WEAPON_SIZES: { id: WeaponSize; label: string }[] = [
  { id: 'fine',       label: 'Fine'       },
  { id: 'diminutive', label: 'Diminutive' },
  { id: 'tiny',       label: 'Tiny'       },
  { id: 'small',      label: 'Small'      },
  { id: 'medium',     label: 'Medium'     },
  { id: 'large',      label: 'Large'      },
  { id: 'huge',       label: 'Huge'       },
  { id: 'gargantuan', label: 'Gargantuan' },
  { id: 'colossal',   label: 'Colossal'   },
];

const SIZE_ORDER: WeaponSize[] = WEAPON_SIZES.map((s) => s.id);

// ---------------------------------------------------------------------------
// Damage progression chart (index = step position)
// ---------------------------------------------------------------------------
export const DAMAGE_CHART = [
  '1',     //  0
  '1d2',   //  1
  '1d3',   //  2
  '1d4',   //  3
  '1d6',   //  4  ← 1d6 threshold
  '1d8',   //  5  ← 1d8 threshold
  '1d10',  //  6
  '2d6',   //  7
  '2d8',   //  8
  '3d6',   //  9
  '3d8',   // 10
  '4d6',   // 11
  '4d8',   // 12
  '6d6',   // 13
  '6d8',   // 14
  '8d6',   // 15
  '8d8',   // 16
  '12d6',  // 17
  '12d8',  // 18
  '16d6',  // 19
] as const;

type ChartEntry = (typeof DAMAGE_CHART)[number];

const IDX_1D6 = DAMAGE_CHART.indexOf('1d6');
const IDX_1D8 = DAMAGE_CHART.indexOf('1d8');
const D6_ON_CHART = [1, 2, 3, 4, 6, 8, 12, 16];
const D8_ON_CHART = [1, 2, 3, 4, 6, 8, 12];

const SMALL_OR_LOWER  = new Set<WeaponSize>(['fine', 'diminutive', 'tiny', 'small']);
const MEDIUM_OR_LOWER = new Set<WeaponSize>(['fine', 'diminutive', 'tiny', 'small', 'medium']);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function moveSize(size: WeaponSize, dir: 1 | -1): WeaponSize {
  const i = SIZE_ORDER.indexOf(size);
  return SIZE_ORDER[Math.max(0, Math.min(SIZE_ORDER.length - 1, i + dir))];
}

/**
 * How many chart positions to move for one size step in the given direction,
 * given the current chart index and current size.
 */
function chartDelta(chartIdx: number, currentSize: WeaponSize, dir: 1 | -1): number {
  if (dir === 1) {
    return SMALL_OR_LOWER.has(currentSize) || chartIdx <= IDX_1D6 ? 1 : 2;
  } else {
    return MEDIUM_OR_LOWER.has(currentSize) || chartIdx <= IDX_1D8 ? 1 : 2;
  }
}

/**
 * Normalise a dice string to a chart index, applying the FAQ substitution rules.
 * Returns -1 if the expression cannot be mapped.
 */
export function findChartIndex(dice: string): number {
  const direct = DAMAGE_CHART.indexOf(dice as ChartEntry);
  if (direct !== -1) return direct;

  const m = dice.match(/^(\d+)d(\d+)$/);
  if (!m) return -1;
  const count = parseInt(m[1]);
  const sides  = parseInt(m[2]);

  // d10 with count >= 2 is handled as a special bypass in the main function
  if (sides === 10) return -1;

  // d4: 2d4=1d8, 3d4=2d6, 4d4=2d8, 5d4=3d6, …
  if (sides === 4 && count > 1) {
    return count % 2 === 0
      ? findChartIndex(`${count / 2}d8`)
      : findChartIndex(`${Math.ceil(count / 2)}d6`);
  }

  // d12: Nd12 → (2N)d6
  if (sides === 12) return findChartIndex(`${count * 2}d6`);

  // d6 off-chart: next-lowest d6 count → use that count of d8 as proxy
  if (sides === 6) {
    const lower = D6_ON_CHART.filter((n) => n <= count);
    if (!lower.length) return IDX_1D6;
    return findChartIndex(`${Math.max(...lower)}d8`);
  }

  // d8 off-chart: next-highest d8 count → use that count of d6 as proxy
  if (sides === 8) {
    const higher = D8_ON_CHART.filter((n) => n >= count);
    if (!higher.length) return DAMAGE_CHART.length - 1;
    return findChartIndex(`${Math.min(...higher)}d6`);
  }

  return -1;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Adjust a pure dice expression (no modifier) for a change in size steps.
 *
 * @param diceExpr  Pure dice string, e.g. "1d8".  Modifier components are
 *                  handled separately and are NOT passed here.
 * @param baseSize  The weapon/creature size at which the base dice were defined.
 * @param sizeSteps Number of size steps to shift: positive = larger, negative = smaller.
 */
export function adjustDiceForSizeChange(
  diceExpr:  string,
  baseSize:  WeaponSize,
  sizeSteps: number,
): string {
  if (sizeSteps === 0 || !diceExpr.trim()) return diceExpr;

  const dir: 1 | -1 = sizeSteps > 0 ? 1 : -1;
  let remaining     = Math.abs(sizeSteps);
  let current       = diceExpr.trim();
  let currentSize   = baseSize;

  while (remaining > 0) {
    // Special: Nd10 (N ≥ 2) bypasses chart entirely
    const d10m = current.match(/^(\d+)d10$/);
    if (d10m) {
      const n = parseInt(d10m[1]);
      if (n >= 2) {
        current = dir === 1 ? `${n * 2}d8` : `${n}d8`;
        currentSize = moveSize(currentSize, dir);
        remaining--;
        continue;
      }
    }

    const idx = findChartIndex(current);
    if (idx === -1) return diceExpr; // Cannot map — return original unchanged

    const newIdx = Math.max(0, Math.min(DAMAGE_CHART.length - 1,
      idx + chartDelta(idx, currentSize, dir) * dir));
    current = DAMAGE_CHART[newIdx];
    currentSize = moveSize(currentSize, dir);
    remaining--;
  }

  return current;
}

/** Display label for a size step adjustment. */
export function sizeStepLabel(steps: number): string {
  if (steps === 0) return 'Base size';
  const abs = Math.abs(steps);
  const word = abs === 1 ? 'step' : 'steps';
  return steps > 0 ? `+${abs} size ${word}` : `−${abs} size ${word}`;
}

/** Effective size name after applying step delta to a base size. */
export function effectiveSizeName(base: WeaponSize, steps: number): string {
  let s = base;
  const dir: 1 | -1 = steps > 0 ? 1 : -1;
  for (let i = 0; i < Math.abs(steps); i++) s = moveSize(s, dir);
  return WEAPON_SIZES.find((w) => w.id === s)?.label ?? s;
}
